import { useState, useEffect } from "react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";

const STAGES = [
  {
    key: "consult",
    tag: "01",
    title: "Consult",
    body:
      "We help unclutter cybersecurity for you by assessing your current security posture, identifying risks, and developing strategic roadmaps through expert cybersecurity consulting — GRC, awareness, CISO-as-a-service.",
  },
  {
    key: "implement",
    tag: "02",
    title: "Implement",
    body:
      "We design, deploy, and integrate best-in-class cybersecurity technologies across identity, cloud, infrastructure, endpoints, SASE, and AI security operations — with hands-on engineering, not slideware.",
  },
  {
    key: "manage",
    tag: "03",
    title: "Manage",
    body:
      "Continuous 24×7 security operations designed to anticipate threats, withstand attacks, recover quickly, and adapt to an evolving landscape — powered by our Cyber Resilience Framework.",
  },
  {
    key: "test",
    tag: "04",
    title: "Test",
    body:
      "We periodically assess the effectiveness of your security controls through penetration testing, bug bounty programs, breach & attack simulations, and hands-on skills assessments.",
  },
];

/**
 * "How we help" — editorial circular lifecycle.
 * Desktop: 4 stage labels at 12/3/6/9 o'clock around a slowly rotating dashed ring,
 * with an active stage that reveals a description panel below.
 * Mobile (<768px): abandon the circle, render a clean editorial vertical stack.
 */
export default function HowWeHelp() {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia("(max-width: 767px)").matches);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <section
      data-testid="how-we-help"
      className="theme-editorial relative bg-white py-24 md:py-28"
      aria-label="How we help"
    >
      {/* Section header */}
      <div className="px-6 md:px-10 mb-16 md:mb-20">
        <div className="mx-auto max-w-[1280px]">
          <div className="grid md:grid-cols-12 gap-6 md:gap-10">
            <div className="md:col-span-6">
              <div className="font-editorial-mono text-[10.5px] tracking-[0.32em] text-[#5B2A86] mb-4">
                [ HOW WE HELP · 04 STAGES ]
              </div>
              <h2 className="font-editorial-display text-[40px] md:text-[64px] leading-[0.98] tracking-[-0.028em] text-[#0B0B10]">
                A continuous cycle of{" "}
                <span className="font-editorial-italic text-[#5B2A86]" style={{ fontStyle: "italic", fontWeight: 400 }}>
                  resilience.
                </span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-[16px] md:text-[18px] leading-[1.55] text-[#4A4A55] max-w-[520px]">
                One partner. Four connected stages. RockLadder engages across the entire security
                lifecycle — anticipating threats, withstanding attacks, recovering quickly, and
                adapting to an ever-evolving landscape.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop circle / Mobile stack */}
      {isMobile ? (
        <MobileStack active={active} setActive={setActive} reduced={reduced} />
      ) : (
        <DesktopCircle active={active} setActive={setActive} reduced={reduced} />
      )}
    </section>
  );
}

/* ────────────────────────  Desktop  ──────────────────────── */

function DesktopCircle({ active, setActive, reduced }) {
  // Compass positions: 12, 3, 6, 9 o'clock (top, right, bottom, left)
  const positions = [
    { key: "top",    tx: 0,    ty: -1 },
    { key: "right",  tx: 1,    ty: 0  },
    { key: "bottom", tx: 0,    ty: 1  },
    { key: "left",   tx: -1,   ty: 0  },
  ];
  const [radius, setRadius] = useState(220);

  useEffect(() => {
    const compute = () => {
      // Radius scales with viewport but capped
      const r = Math.min(230, window.innerWidth * 0.16);
      setRadius(r);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  return (
    <div className="px-6 md:px-10">
      <div className="mx-auto max-w-[1280px]">
        <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
          {/* Circle */}
          <div className="md:col-span-6 flex justify-center">
            <div
              className="relative"
              style={{
                width: "min(560px, 44vw)",
                height: "min(560px, 44vw)",
              }}
            >
              {/* Rotating dashed ring */}
              <motion.div
                aria-hidden
                animate={reduced ? {} : { rotate: 360 }}
                transition={reduced ? {} : { duration: 40, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full border border-dashed"
                style={{ borderColor: "rgba(11,11,16,0.15)", borderWidth: "1.5px" }}
              />
              {/* Inner subtle ring */}
              <div
                aria-hidden
                className="absolute rounded-full border"
                style={{
                  inset: "18%",
                  borderColor: "rgba(11,11,16,0.05)",
                }}
              />

              {/* Center hub */}
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center max-w-[52%]">
                  <div className="font-editorial-mono text-[10px] tracking-[0.32em] text-[#8A8A8A] mb-2">
                    LIFECYCLE
                  </div>
                  <div
                    className="font-editorial-italic text-[#4A4A55] leading-[1.05]"
                    style={{
                      fontStyle: "italic",
                      fontWeight: 400,
                      fontSize: "clamp(20px, 2.4vw, 28px)",
                    }}
                  >
                    Cyber Resilience
                  </div>
                </div>
              </div>

              {/* Stage nodes */}
              {STAGES.map((s, i) => {
                const p = positions[i];
                const isActive = i === active;
                return (
                  <div
                    key={s.key}
                    className="absolute"
                    style={{
                      left: `calc(50% + ${p.tx * radius}px)`,
                      top: `calc(50% + ${p.ty * radius}px)`,
                    }}
                  >
                    <motion.button
                      type="button"
                      onClick={() => setActive(i)}
                      onMouseEnter={() => setActive(i)}
                      data-testid={`stage-node-${s.key}`}
                      initial={{ opacity: 0, y: p.ty * -12, x: p.tx * -12 }}
                      whileInView={{ opacity: 1, y: 0, x: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{
                        duration: 0.7,
                        ease: [0.22, 1, 0.36, 1],
                        delay: 0.15 + i * 0.12,
                      }}
                      className="group cursor-pointer -translate-x-1/2 -translate-y-1/2 block"
                      aria-label={`${s.title} — stage ${s.tag}`}
                      aria-pressed={isActive}
                    >
                    <div className="flex flex-col items-center">
                      {/* Node dot */}
                      <span
                        className="rounded-full transition-all duration-300"
                        style={{
                          width: isActive ? 14 : 8,
                          height: isActive ? 14 : 8,
                          background: isActive ? "#5B2A86" : "#0B0B10",
                          boxShadow: isActive ? "0 0 0 6px rgba(91,42,134,0.12)" : "none",
                        }}
                      />
                      <span
                        className="font-editorial-mono mt-3 tracking-[0.28em] transition-colors duration-300"
                        style={{
                          fontSize: 10,
                          color: isActive ? "#5B2A86" : "#8A8A8A",
                        }}
                      >
                        /{s.tag}
                      </span>
                      <span
                        className="font-editorial-display mt-1 transition-colors duration-300 relative"
                        style={{
                          fontSize: "clamp(20px, 2.2vw, 26px)",
                          color: isActive ? "#0B0B10" : "#4A4A55",
                          letterSpacing: "-0.02em",
                          lineHeight: 1.1,
                        }}
                      >
                        {s.title}
                        {/* Active underline */}
                        <motion.span
                          initial={false}
                          animate={{ scaleX: isActive ? 1 : 0 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          className="absolute -bottom-1 left-0 right-0 h-[1.5px] bg-[#F5B841] origin-left"
                        />
                      </span>
                    </div>
                    </motion.button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Description panel */}
          <div className="md:col-span-5 md:col-start-8">
            <div className="min-h-[280px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={STAGES[active].key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`stage-detail-${STAGES[active].key}`}
                >
                  <div className="font-editorial-mono text-[10px] tracking-[0.32em] text-[#5B2A86] mb-4">
                    STAGE /{STAGES[active].tag}
                  </div>
                  <h3
                    className="font-editorial-display leading-[1] tracking-[-0.02em] text-[#0B0B10] mb-6"
                    style={{ fontSize: "clamp(36px, 4.2vw, 56px)" }}
                  >
                    {STAGES[active].title}
                    <span className="font-editorial-italic text-[#5B2A86]" style={{ fontStyle: "italic", fontWeight: 400 }}>.</span>
                  </h3>
                  <p className="text-[16px] md:text-[17px] leading-[1.6] text-[#4A4A55] max-w-[520px]">
                    {STAGES[active].body}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Stage progress bar */}
              <div className="mt-10 flex items-center gap-2 max-w-[520px]">
                {STAGES.map((s, i) => (
                  <button
                    key={s.key}
                    onClick={() => setActive(i)}
                    aria-label={`View ${s.title}`}
                    data-testid={`stage-progress-${s.key}`}
                    className="flex-1 h-[2px] transition-colors duration-300"
                    style={{
                      background: i === active ? "#0B0B10" : "rgba(11,11,16,0.12)",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────  Mobile  ──────────────────────── */

function MobileStack({ active, setActive, reduced }) {
  return (
    <div className="px-6">
      <div className="mx-auto max-w-[560px] space-y-0 border-t border-[rgba(11,11,16,0.10)]">
        {STAGES.map((s, i) => {
          const isActive = i === active;
          return (
            <motion.button
              key={s.key}
              type="button"
              onClick={() => setActive(isActive ? -1 : i)}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
              data-testid={`stage-mobile-${s.key}`}
              className="block w-full text-left border-b border-[rgba(11,11,16,0.10)] py-6 group"
              aria-expanded={isActive}
            >
              <div className="flex items-start gap-4">
                <span className="font-editorial-mono text-[10px] tracking-[0.28em] text-[#5B2A86] pt-2 shrink-0 w-8">
                  /{s.tag}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <h3
                      className="font-editorial-display text-[#0B0B10]"
                      style={{ fontSize: 28, lineHeight: 1.05, letterSpacing: "-0.02em" }}
                    >
                      {s.title}
                    </h3>
                    <motion.span
                      animate={{ rotate: isActive ? 45 : 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="font-editorial-mono text-[18px] text-[#4A4A55] leading-none"
                    >
                      +
                    </motion.span>
                  </div>
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.p
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="text-[14.5px] leading-[1.55] text-[#4A4A55] overflow-hidden"
                      >
                        {s.body}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
